/* =====================================================================
 * FilterDropdown — 다중 체크박스 선택형 필터 드롭다운.
 *
 * 사용 예 (CAD 파일 섹션의 확장자 필터):
 *   <FilterDropdown
 *     label="확장자"
 *     options={[
 *       { value: '.pdf', label: 'PDF' },
 *       { value: '.png', label: 'PNG' },
 *     ]}
 *     selected={['.pdf']}
 *     onChange={(next) => store.setFileFilter('extensions', next)}
 *   />
 *
 * 디자인 일관성: 색·간격·라운드 모두 theme 토큰만 사용. 도메인 비종속.
 *
 * 인터랙션:
 *   - 트리거 클릭 → popup 토글. popup 은 portal 없이 absolute 로 트리거
 *     아래 (right-aligned) 에 렌더. 외부 클릭 / Escape 으로 닫힘.
 *   - 옵션 클릭 = 즉시 선택/해제 + onChange 호출 (별도 "적용" 버튼 없음).
 *   - 활성 옵션 0개면 트리거 라벨은 "라벨" 그대로, 1개 이상이면 "라벨 (N)".
 *   - 옵션 0개면 트리거 비활성화.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Check, ChevronDown } from 'lucide-react';

export interface FilterDropdownOption {
  value: string;
  label: string;
}

export interface FilterDropdownProps {
  label: string;
  options: readonly FilterDropdownOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** 트리거 비활성화. options 가 비어있어도 자동으로 비활성된다. */
  disabled?: boolean;
  /** popup 최소 폭. 기본 200px. */
  minWidth?: number;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  disabled = false,
  minWidth = 200,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const isDisabled = disabled || options.length === 0;
  const activeCount = selected.length;
  const triggerLabel = activeCount > 0 ? `${label} (${activeCount})` : label;

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleOption = useCallback(
    (value: string) => {
      const next = new Set(selectedSet);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      // 옵션 정의 순서를 유지해 안정된 query string 생성.
      const ordered = options
        .map((o) => o.value)
        .filter((v) => next.has(v));
      onChange(ordered);
    },
    [onChange, options, selectedSet],
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <Wrap>
      <TriggerBtn
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        $active={activeCount > 0}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <TriggerLabel>{triggerLabel}</TriggerLabel>
        <ChevronDown size={14} />
      </TriggerBtn>
      {open && !isDisabled && (
        <Popup
          ref={popupRef}
          role="listbox"
          aria-multiselectable
          style={{ minWidth }}
        >
          {options.map((opt) => {
            const checked = selectedSet.has(opt.value);
            return (
              <OptionRow
                key={opt.value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => toggleOption(opt.value)}
                $checked={checked}
              >
                <OptionCheck $checked={checked}>
                  {checked ? <Check size={12} strokeWidth={3} /> : null}
                </OptionCheck>
                <OptionLabel>{opt.label}</OptionLabel>
              </OptionRow>
            );
          })}
        </Popup>
      )}
    </Wrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  /* 헤더 안 다른 액션(검색/뷰토글/업로드) 과 시각 무게가 비슷하도록 키운다. */
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,149,0,0.16)'
        : 'rgba(255,149,0,0.10)'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(0,0,0,0.03)'};
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? theme.colors.accent
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.10)'
          : 'rgba(0,0,0,0.08)'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.textSecondary};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark'
          ? 'rgba(255,149,0,0.22)'
          : 'rgba(255,149,0,0.14)'
        : theme.colors.bgHover};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.accent : theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 30;
  padding: 4px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-height: 320px;
  overflow-y: auto;
`;

const OptionRow = styled.button<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: ${({ $checked, theme }) =>
    $checked
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.03)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const OptionCheck = styled.span<{ $checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.accent : 'transparent'};
  border: 1.5px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.accent : theme.colors.borderInput};
  color: #ffffff;
`;

const OptionLabel = styled.span`
  white-space: nowrap;
`;
